import type { loader as rootLoader } from '#app/root.tsx'
import type { loader } from '../index.tsx'
import { useState, useCallback } from 'react'
import {
	APIProvider,
	Map,
	AdvancedMarker,
	useAdvancedMarkerRef,
	InfoWindow,
} from '@vis.gl/react-google-maps'
import { useRouteLoaderData } from '@remix-run/react'
import { Icon } from '#app/components/ui/icon.tsx'

type MapCustomer = {
	id: number
	name: string | null
	lat: string | null
	long: string | null
	city: string | null
	state: string | null
	address: string | null
	zipcode: string | null
	leafCustomerMetadata: {
		latitude: number | null
		longitude: number | null
	} | null
}

interface DispensaryMapProps {
	dispensaries: { leafCustomer: MapCustomer | null }[]
}

//  helper function to check for valid coordinates
const hasValidCoordinates = (customer: MapCustomer) => {
	return (
		(customer.lat && customer.long) ||
		(customer.leafCustomerMetadata?.latitude &&
			customer.leafCustomerMetadata?.longitude)
	)
}

//  helper function to get coordinates
const getCoordinates = (customer: MapCustomer) => {
	return {
		lat:
			customer.leafCustomerMetadata?.latitude ?? parseFloat(customer.lat ?? ''),
		lng:
			customer.leafCustomerMetadata?.longitude ??
			parseFloat(customer.long ?? ''),
	}
}

// TODO: set default center in dashboard settings
// Current default center is Portland, Maine
const DEFAULT_CENTER = {
	lat: 43.65107,
	lng: -70.2526,
} as const

export default function DispensaryMap({ dispensaries }: DispensaryMapProps) {
	const { ENV } = useRouteLoaderData<typeof rootLoader>('root') || {}
	const [openWindowCustomerId, setOpenWindowCustomerId] = useState<
		number | null
	>(null)

	//  Finding first valid customer to set as map center
	const firstCustomerWithCoords = dispensaries.find(
		(c) => c.leafCustomer && hasValidCoordinates(c.leafCustomer),
	)?.leafCustomer

	const position = firstCustomerWithCoords
		? getCoordinates(firstCustomerWithCoords)
		: DEFAULT_CENTER

	return (
		<APIProvider apiKey={ENV!.GOOGLE_MAPS_API_KEY}>
			<Map
				defaultCenter={position}
				defaultZoom={8}
				className="h-[500px]"
				mapId={'dispensary-map'}
			>
				{dispensaries.map((c, idx) =>
					c.leafCustomer && hasValidCoordinates(c.leafCustomer) ? (
						<MarkerWithInfowindow
							key={c.leafCustomer.id} // 6. Use stable key instead of index
							customer={c.leafCustomer}
							isOpen={openWindowCustomerId === c.leafCustomer.id}
							setOpenWindowCustomerId={setOpenWindowCustomerId}
						/>
					) : null,
				)}
			</Map>
		</APIProvider>
	)
}

// Marker with infowindow component
const MarkerWithInfowindow = ({
	customer,
	isOpen,
	setOpenWindowCustomerId,
}: {
	customer: MapCustomer
	isOpen: boolean
	setOpenWindowCustomerId: (id: number | null) => void
}) => {
	const [markerRef, marker] = useAdvancedMarkerRef()

	// 8. Use useCallback for event handlers
	const handleMarkerClick = useCallback(() => {
		setOpenWindowCustomerId(customer.id)
	}, [customer.id, setOpenWindowCustomerId])

	const handleCloseClick = useCallback(() => {
		setOpenWindowCustomerId(null)
	}, [setOpenWindowCustomerId])

	return (
		<>
			<AdvancedMarker
				//@ts-ignore
				ref={markerRef}
				onClick={handleMarkerClick}
				position={getCoordinates(customer)}
				title={customer.name ?? undefined}
			/>
			{isOpen && (
				<InfoWindow
					headerDisabled={true}
					anchor={marker}
					maxWidth={300}
					onCloseClick={handleCloseClick}
				>
					<div>
						<span className="flex justify-between">
							<h3 className="mb-1 font-bold">{customer.name}</h3>
							<Icon
								name="cross-1"
								className="ml-4 h-4 w-4 cursor-pointer text-gray-400 group-hover:text-green-600"
								onClick={handleCloseClick}
							/>
						</span>

						<p>
							{customer.address} <br />
							{customer.city}, {customer.state} {customer.zipcode}
						</p>

						<a
							target="_blank"
							href={
								'http://maps.google.com/maps?q=' +
								encodeURIComponent(
									customer.address +
										' ' +
										customer.city +
										' ' +
										customer.state +
										' ' +
										customer.zipcode,
								)
							}
							className="group mt-2 flex text-sm hover:text-green-600"
							rel="noreferrer"
						>
							<Icon
								name="sewing-pin"
								className="text-primary/80 group-hover:text-green-600"
							/>
							<span className="ml-1">Get Directions</span>
						</a>
					</div>
				</InfoWindow>
			)}
		</>
	)
}
